import dayjs from 'dayjs';
import { defineStore } from 'pinia';

import { useDataFormat } from '@/composables/useDataFormat.js';
import { aanvullendeDekkingenStore } from '@/stores/aanvullendedekkingen.js';
import { formStore } from '@/stores/form.js';
import { pakketStore } from '@/stores/pakket.js';
import { vragenStore } from '@/stores/vragen.js';

export const verzekeringStore = defineStore({
  id: 'verzekering',
  state: () => ({
    verzekering: {},
  }),
  getters: {
    getHasVereende() {
      // Check if the insurance object is not empty and if the insurance company is Vereende
      if (Object.keys(this.verzekering).length > 0) {
        for (const verz in this.verzekering) {
          if (this.verzekering[verz].maatschappij && this.verzekering[verz].maatschappij.toLowerCase().includes("vereende")) {
            return true;
          }
        }
      }
      return false;
    },
  },
  actions: {
    getAllInsuranceNames() {
      return Object.keys(this.verzekering);
    },
    getTotalProvisie() {
      return Object.values(this.verzekering).reduce((total, current) => {
        return total + (Number(current.provisie) || 0);
      }, 0);
    },
    getTotalPremie(verzekeringType) {
      return this.verzekering[verzekeringType].premie;
    },
    getFirstPremie(verzekeringType) {
      return this.verzekering[verzekeringType].eerstePremie;
    },
    getServiceBijdrage(verzekeringType) {
      return this.verzekering[verzekeringType].servicebijdrage;
    },
    getDekking(verzekeringType) {
      return this.verzekering[verzekeringType]?.dekking;
    },
    getWaarbrogsom(verzekeringType) {
      return this.verzekering[verzekeringType]?.waarborgsom;
    },
    getTotalPremieWithAanvullend(verzekeringType, includeServiceBijdrage = true) {
      const aanvullendeDekkingen = aanvullendeDekkingenStore();
      const serviceBijdrage = includeServiceBijdrage ? this.verzekering[verzekeringType].servicebijdrage : 0;
      return (
        this.getTotalPremie(verzekeringType) +
        serviceBijdrage +
        aanvullendeDekkingen.getTotalAmountSelected
      );
    },
    getFirstPremieWithAanvullend(verzekeringType) {
      const aanvullendeDekkingen = aanvullendeDekkingenStore();
      return this.getFirstPremie(verzekeringType) + aanvullendeDekkingen.getTotalAmountSelected;
    },
    getPremieDetail(verzekeringType, detailkey) {
      let verzekering = this.verzekering[verzekeringType];

      if (verzekering) {
        for (let [key, value] of Object.entries(verzekering)) {
          if (typeof value === 'object' && (key === 'primairedetails' || key === 'secundairedetails')) {
            for (const key in value) {
              let detail = value[key];
              if (detail.Naam === detailkey) {
                return detail.Waarde;
              }
            }
          }
        }
      }
      return false;
    },
    setPremie(verzekeringType, premieGeselecteerd) {
      this.verzekering[verzekeringType] = premieGeselecteerd;
    },
    synchronizePackageStartDate(verzekeringType) {
      const { capitalizeFirstLetter } = useDataFormat();

      const pakket = pakketStore();
      const vragen = vragenStore();

      // Replace 'doorlopendereis' with 'doorlopendeReis'
      if (verzekeringType === 'doorlopendereis') verzekeringType = 'doorlopendeReis';

      let ingangsdatum = this.verzekering[capitalizeFirstLetter(verzekeringType)].ingangsdatum;
      let ingangsdatumType = vragen[verzekeringType.toLowerCase()].IngangsDatum;

      // If the package has no start date, set it to the current date
      if (pakket.ingangsdatum === '') {
        pakket.ingangsdatum = ingangsdatum;

        // Set the start date and start date type for all insurances
        Object.keys(this.verzekering).forEach((verz) => {
          // Replace 'doorlopendereis' with 'doorlopendeReis'
          if (verz === 'doorlopendereis') verz = 'doorlopendeReis';

          this.verzekering[verz].ingangsdatum = ingangsdatum;
          vragen[verz.toLowerCase()].IngangsDatum = ingangsdatumType;
        });
      }
    },
    hasKenmerk(verzekeringType) {
      return verzekeringType in this.verzekering ? this.verzekering[verzekeringType].kenmerk !== null : false;
    },
    verzekeringExists(verzekeringType) {
      const { capitalizeFirstLetter } = useDataFormat();

      if (!verzekeringType) return false;

      verzekeringType = capitalizeFirstLetter(verzekeringType);

      return Object.prototype.hasOwnProperty.call(this.$state.verzekering, verzekeringType);
    },
    // function with funnel entry check
    verzekeringExistsWithFilter(verzekeringType) {
      // Normalize the insurance type by capitalizing the first letter
      const { capitalizeFirstLetter } = useDataFormat();

      // Return false if no insurance type is provided
      if (!verzekeringType) return false;

      // Normalize the insurance type
      verzekeringType = capitalizeFirstLetter(verzekeringType);
      const form = formStore();
      // Check if the funnel entry is either 'inboedel' or 'woonhuis'
      if (form.funnelEntry === 'inboedel' || form.funnelEntry === 'woonhuis') {
        // If the insurance type is 'contentsAndHouse', check if both are selected
        if (verzekeringType === 'InboedelEnWoonhuis') {
          return (
            Object.prototype.hasOwnProperty.call(this.$state.verzekering, 'Inboedel') &&
            Object.prototype.hasOwnProperty.call(this.$state.verzekering, 'Woonhuis')
          );
        } else {
          // If the insurance type is 'inboedel' or 'woonhuis', check if the corresponding insurance exists
          // and the other does not
          const andereType = verzekeringType === 'Inboedel' ? 'Woonhuis' : 'Inboedel';
          return (
            Object.prototype.hasOwnProperty.call(this.$state.verzekering, verzekeringType) &&
            !Object.prototype.hasOwnProperty.call(this.$state.verzekering, andereType)
          );
        }
      }

      // Return true if the insurance type exists
      return Object.prototype.hasOwnProperty.call(this.$state.verzekering, verzekeringType);
    },
    toggleVerzekering(verzekeringType, isAdditionalCoverageOption) {
      const { capitalizeFirstLetter } = useDataFormat();
      const form = formStore();

      verzekeringType = capitalizeFirstLetter(verzekeringType);
      // Check if the funnel entry is either 'inboedel' or 'woonhuis'
      if (form.funnelEntry === 'inboedel' || form.funnelEntry === 'woonhuis') {
        verzekeringType = capitalizeFirstLetter(verzekeringType);

        // Check if both 'inboedel' and 'woonhuis' exist in the store
        const bothExist =
          Object.prototype.hasOwnProperty.call(this.$state.verzekering, 'Inboedel') &&
          Object.prototype.hasOwnProperty.call(this.$state.verzekering, 'Woonhuis');

        // If the insurance type is 'InboedelEnWoonhuis' and both 'inboedel' and 'woonhuis'
        if (verzekeringType === 'InboedelEnWoonhuis') {
          // If both 'inboedel' and 'woonhuis' exist in the store, delete both
          if (bothExist) {
            delete this.verzekering['Inboedel'];
            delete this.verzekering['Woonhuis'];
            return;
          }
          // If both 'inboedel' and 'woonhuis' do not exist in the store, initialize both
          if (!bothExist) {
            this.verzekering['Inboedel'] = {};
            this.verzekering['Woonhuis'] = {};
            return;
          }
        } else if(verzekeringType === 'Inboedel' || verzekeringType === 'Woonhuis'){
          const typeToDelete = verzekeringType === 'Inboedel' ? 'Woonhuis' : 'Inboedel';
          delete this.verzekering[typeToDelete];
        } 
      }
      // Handle the insurance type differently if the funnel entry is neither 'inboedel' nor 'woonhuis'
      this.handleVerzekeringType(verzekeringType, isAdditionalCoverageOption);
    },
    handleVerzekeringType(verzekeringType, isAdditionalCoverageOption) {
      // or if the insurance type is 'InboedelEnWoonhuis', then handle as before
      if (verzekeringType === 'InboedelEnWoonhuis') {
        const types = ['Inboedel', 'Woonhuis']; // Define the types to toggle between
        types.forEach((type) => {
          // initialize insurance
          this.verzekering[type] = {};
        });
      } else {
        // For other insurance types, toggle its existence in the state and isAdditionalCoverageOption (no combination component) is true
        if (Object.prototype.hasOwnProperty.call(this.$state.verzekering, verzekeringType) && isAdditionalCoverageOption) {
          delete this.verzekering[verzekeringType];
        } else {
          this.verzekering[verzekeringType] = {};
        }
      }
    },
    initializeVerzekering(verzekeringType) {
      this.verzekering[verzekeringType] = {};
      this.verzekering[verzekeringType].ingangsdatum = dayjs(new Date()).format('YYYY-MM-DD');
      this.verzekering[verzekeringType].kenmerk = null;
    },
    resetVerzekering(verzekeringType) {
      if (verzekeringType in this.verzekering) {
        const tempIngangsdatum = this.verzekering[verzekeringType].ingangsdatum;
        this.$reset();
        this.verzekering[verzekeringType].ingangsdatum = tempIngangsdatum;
        this.verzekering[verzekeringType].kenmerk = null;
      }
    },
  },
  persist: true,
});
